import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Wallet } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeCreditsSystemModeFromPlatformData } from '@/lib/creditsPlatformMode';
import { UnitMembership } from '@/types';

export function EventHeaderCredits({ unitId }: { unitId?: string }) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.isAdmin === true;
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditsSystemMode, setCreditsSystemMode] = useState<'enabled' | 'disabled' | 'admin_only'>('enabled');

  useEffect(() => {
    if (!unitId) return;
    const ref = doc(db, 'units', unitId);
    let cancelled = false;

    // Refresh persisted Firestore cache from the server first so the initial listener
    // emission is not a stale offline snapshot when online.
    Promise.resolve(getDocFromServer(ref))
      .then((snap) => {
        if (cancelled) return;
        if (snap && typeof snap.exists === 'function' && snap.exists()) {
          const v = snap.data?.()?.creditBalance;
          setCreditBalance(typeof v === 'number' ? v : 0);
        } else {
          setCreditBalance(0);
        }
      })
      .catch(() => {
        // Offline or transient failure: onSnapshot still delivers cache or server when available.
      });

    const unsub = onSnapshot(ref, (snap) => {
      const v = snap.data()?.creditBalance;
      setCreditBalance(typeof v === 'number' ? v : 0);
    });
    return () => {
      cancelled = true;
      if (typeof unsub === 'function') {
        unsub();
      }
    };
  }, [unitId]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const s = await getDoc(doc(db, 'systemSettings', 'platform'));
        const mode = normalizeCreditsSystemModeFromPlatformData(s.data() as Record<string, unknown> | undefined);
        setCreditsSystemMode(mode);
      } catch {
        // ignore
      }
    };
    loadSettings();
  }, []);

  if (!unitId) return null;

  const userRole = userProfile?.units?.find((u: UnitMembership) => u.unitId === unitId)?.role;
  const canSeeCredits = creditsSystemMode === 'enabled' || (creditsSystemMode === 'admin_only' && isAdmin);

  if (!canSeeCredits || (userRole !== 'Secretary' && userRole !== 'Treasurer' && !isAdmin)) {
    return null;
  }

  return (
    <Button
      variant="outline"
      className="w-full shrink-0 sm:w-auto ml-auto"
      onClick={() => navigate(`/unit/${unitId}/credits`)}
    >
      <Wallet className="mr-2 h-4 w-4" />
      <span className={creditBalance !== null && creditBalance < 0 ? 'text-destructive font-semibold' : ''}>
        {creditBalance === null ? '…' : creditBalance} Credits
      </span>
    </Button>
  );
}
